import { useQuery } from '@tanstack/react-query';

import * as callsenseApi from './callsense.api';

import type { CallsenseCallsFilters, CallsenseFilters } from './callsense.api';

/** Shared across every read here - the dashboard is not a live feed. */
const READ_OPTIONS = { staleTime: 30_000, refetchOnWindowFocus: false } as const;

export const useCallsenseOverview = (filters: CallsenseFilters) =>
  useQuery({
    queryKey: ['callsense', 'overview', filters] as const,
    queryFn: () => callsenseApi.getCallsenseOverview(filters),
    ...READ_OPTIONS,
  });

export const useCallsenseCallingReport = (filters: CallsenseFilters) =>
  useQuery({
    queryKey: ['callsense', 'calling-report', filters] as const,
    queryFn: () => callsenseApi.getCallsenseCallingReport(filters),
    ...READ_OPTIONS,
  });

export const useCallsenseCalls = (filters: CallsenseCallsFilters) =>
  useQuery({
    queryKey: ['callsense', 'calls', filters] as const,
    queryFn: () => callsenseApi.getCallsenseCalls(filters),
    ...READ_OPTIONS,
  });

/** Full analysis record; only fires once a call is opened. Pass `leadId` to
 *  use the lead-context route (ADMIN + SALES_LEAD, no dashboard permission). */
export const useCallsenseCall = (id: string | null, leadId?: string) =>
  useQuery({
    queryKey: ['callsense', 'call', id, leadId ?? null] as const,
    queryFn: () => callsenseApi.getCallsenseCall(id!, leadId),
    enabled: Boolean(id),
    ...READ_OPTIONS,
  });

/** CallSense insight rows for a lead's CallerDesk sids, keyed by sid. */
export const useLeadCallInsights = (leadId: string, sids: string[]) =>
  useQuery({
    queryKey: ['callsense', 'lead-insights', leadId, sids] as const,
    queryFn: () => callsenseApi.getLeadCallInsights(leadId, sids),
    enabled: sids.length > 0,
    ...READ_OPTIONS,
  });
